import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import {
  addDocument,
  addInvolved,
  addInvolvedFailure,
  addInvolvedSuccess,
  addLink,
  addLinkFailure,
  addLinkSuccess,
  addRelation,
  addRelationFailure,
  addRelationSuccess,
  fetchAvailableStatuses,
  fetchAvailableStatusesFailure,
  fetchAvailableStatusesSuccess,
  fetchDossier,
  fetchDossierData,
  fetchDossierDataSuccess,
  fetchDossierFailure,
  fetchDossierSuccess,
  removeDocument,
  removeDocumentFailure,
  removeDocumentSuccess,
  removeInvolved,
  removeInvolvedFailure,
  removeInvolvedSuccess,
  removeLink,
  removeLinkFailure,
  removeLinkSuccess,
  removeRelation,
  removeRelationFailure,
  removeRelationSuccess,
  updateDossierHeaderSuccess
} from './dossiers.actions';
import { IDocument, IDossier } from './dossiers.models';

import { DOSSIERS_INVOLVED_CONTEXT, DOSSIERS_RELATIONS_CONTEXT } from '~/dossiers/dossier-detail/dossier-detail.const';
import { Status } from '~/ui/cardview-grid/cardview-grid.types';

export interface DossierState extends EntityState<IDossier> {
  loadingDossier: boolean;
  loadingStatuses: boolean;

  addRelationLoading: boolean;
  addLinkLoading: boolean;
  addInvolvedLoading: boolean;

  removeRelationLoading: boolean;
  removeLinkLoading: boolean;
  removeInvolvedLoading: boolean;
  removeDocumentLoading: boolean;

  availableStatuses: Status[];
  error?: any;
}

export const dossierAdapter: EntityAdapter<IDossier> = createEntityAdapter<IDossier>();

export const initialState: DossierState = dossierAdapter.getInitialState({
  // set initial required properties
  loadingDossier: false,
  loadingStatuses: false,

  addRelationLoading: false,
  addLinkLoading: false,
  addInvolvedLoading: false,

  removeRelationLoading: false,
  removeLinkLoading: false,
  removeInvolvedLoading: false,
  removeDocumentLoading: false,

  availableStatuses: [],
  error: null
});

const reducer = createReducer(
  initialState,
  on(fetchDossier, (state, { showLoading }) => ({
    ...state,
    loadingDossier: showLoading,
    error: null
  })),
  on(fetchDossierSuccess, (state, { data }) =>
    dossierAdapter.upsertOne(
      {
        ...data,
        error: null
      },
      {
        ...state,
        loadingDossier: false
      }
    )
  ),
  on(fetchDossierFailure, (state, { id, error }) => {
    return dossierAdapter.upsertOne(
      {
        id,
        ...state.entities[id],
        error
      },
      {
        ...state,
        loadingDossier: false
      }
    );
  }),
  on(fetchDossierData, (state, { id, dataKey, context }) => {
    if (!context) {
      return dossierAdapter.upsertOne(
        {
          id,
          ...state.entities[id],
          [dataKey]: {
            loading: true
          }
        },
        {
          ...state
        }
      );
    } else {
      return dossierAdapter.upsertOne(
        {
          id,
          ...state.entities[id],
          [dataKey]: {
            ...state.entities[id]?.[dataKey],
            [context]: {
              loading: true
            }
          }
        },
        {
          ...state
        }
      );
    }
  }),
  on(fetchDossierDataSuccess, (state, { id, dataKey, data, context }) => {
    if (!context) {
      return dossierAdapter.upsertOne(
        {
          id,
          ...state.entities[id],
          [dataKey]: {
            loading: false,
            data
          }
        },
        {
          ...state
        }
      );
    } else {
      return dossierAdapter.upsertOne(
        {
          id,
          ...state.entities[id],
          [dataKey]: {
            ...state.entities[id]?.[dataKey],
            [context]: {
              loading: false,
              data
            }
          }
        },
        {
          ...state
        }
      );
    }
  }),
  on(fetchDossierFailure, (state, { error }) => ({ ...state, error, loadingDossier: false })),
  on(updateDossierHeaderSuccess, (state, { data }) => {
    return dossierAdapter.upsertOne(data, {
      ...state
    });
  }),
  on(fetchAvailableStatuses, (state) => ({
    ...state,
    loadingStatuses: true,
    error: null
  })),
  on(fetchAvailableStatusesSuccess, (state, { data }) => ({
    ...state,
    loadingStatuses: false,
    availableStatuses: data
  })),
  on(fetchAvailableStatusesFailure, (state, { error }) => ({ ...state, error, loadingStatuses: false })),
  on(addDocument, (state, { dossierId, document }) => {
    return dossierAdapter.upsertOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        documents: {
          loading: state.entities[dossierId].documents.loading,
          data: [...(state.entities[dossierId].documents?.data || []), document]
        }
      },
      state
    );
  }),
  on(removeDocument, (state) => ({
    ...state,
    removeDocumentLoading: true,
    error: null
  })),
  on(removeDocumentSuccess, (state, { dossierId, documentId }) => {
    return dossierAdapter.upsertOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        documents: {
          loading: state.entities[dossierId].documents.loading,
          data: state.entities[dossierId].documents?.data.filter((doc: IDocument) => doc.documentId !== documentId)
        }
      },
      {
        ...state,
        removeDocumentLoading: false
      }
    );
  }),
  on(removeDocumentFailure, (state, { error }) => ({ ...state, error, removeDocumentLoading: false })),
  on(addRelation, (state) => ({
    ...state,
    addRelationLoading: true,
    error: null
  })),
  on(addRelationSuccess, (state, { dossierId, context, relation }) => {
    const relations = cloneDeep(state.entities[dossierId].relations);
    relations[context].data.push(relation);

    return dossierAdapter.upsertOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        relations
      },
      {
        ...state,
        addRelationLoading: false
      }
    );
  }),
  on(addRelationFailure, (state, { error }) => ({ ...state, error, addRelationLoading: false })),
  on(removeRelation, (state) => ({
    ...state,
    removeRelationLoading: true,
    error: null
  })),
  on(removeRelationSuccess, (state, { dossierId, relationId, context }) => {
    const relations = cloneDeep(state.entities[dossierId].relations);

    relations[context].data = relations[context].data.filter((item) => {
      return item.id != relationId;
    });

    return dossierAdapter.setOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        relations
      },
      {
        ...state,
        removeRelationLoading: false
      }
    );
  }),
  on(removeRelationFailure, (state, { error }) => ({ ...state, error, removeRelationLoading: false })),
  on(addLink, (state) => ({
    ...state,
    addLinkLoading: true,
    error: null
  })),
  on(addLinkSuccess, (state, { dossierId, link }) => {
    const relations = cloneDeep(state.entities[dossierId].relations);
    relations[DOSSIERS_RELATIONS_CONTEXT.DOSSIER].data.push(link);

    return dossierAdapter.upsertOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        relations
      },
      {
        ...state,
        addLinkLoading: false
      }
    );
  }),
  on(addLinkFailure, (state, { error }) => ({ ...state, error, addLinkLoading: false })),
  on(removeLink, (state) => ({
    ...state,
    removeLinkLoading: true,
    error: null
  })),
  on(removeLinkSuccess, (state, { dossierId, linkId }) => {
    const relations = cloneDeep(state.entities[dossierId].relations);

    relations[DOSSIERS_RELATIONS_CONTEXT.DOSSIER].data = relations[DOSSIERS_RELATIONS_CONTEXT.DOSSIER].data.filter(
      (item) => {
        return item.id != linkId;
      }
    );

    return dossierAdapter.setOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        relations
      },
      {
        ...state,
        removeLinkLoading: false
      }
    );
  }),
  on(removeLinkFailure, (state, { error }) => ({ ...state, error, removeLinkLoading: false })),
  on(addInvolved, (state) => ({
    ...state,
    addInvolvedLoading: true,
    error: null
  })),
  on(addInvolvedSuccess, (state, { dossierId, context, involved }) => {
    const items = cloneDeep(state.entities[dossierId].involved);
    items.data.involved[DOSSIERS_INVOLVED_CONTEXT[context]].push(involved);

    return dossierAdapter.upsertOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        involved: items
      },
      {
        ...state,
        addInvolvedLoading: false
      }
    );
  }),
  on(addInvolvedFailure, (state, { error }) => ({ ...state, error, addInvolvedLoading: false })),
  on(removeInvolved, (state) => ({
    ...state,
    removeInvolvedLoading: true,
    error: null
  })),
  on(removeInvolvedSuccess, (state, { dossierId, context, involvedId }) => {
    const involved = cloneDeep(state.entities[dossierId].involved);

    involved.data.involved[DOSSIERS_INVOLVED_CONTEXT[context]] = involved.data.involved[
      DOSSIERS_INVOLVED_CONTEXT[context]
    ].filter((item) => {
      return item.key != involvedId;
    });

    return dossierAdapter.setOne(
      {
        id: dossierId,
        ...state.entities[dossierId],
        involved
      },
      {
        ...state,
        removeInvolvedLoading: false
      }
    );
  }),
  on(removeInvolvedFailure, (state, { error }) => ({ ...state, error, removeInvolvedLoading: false }))
);

export function dossierReducer(state: DossierState | undefined, action: Action) {
  return reducer(state, action);
}
